import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, Settings, Users, FileText, Pill, Receipt,
  Upload, Send, CheckCircle, AlertCircle, Download,
  Trash2, Search, RefreshCw, PenLine, Mail,
  FileCheck, Euro, Building2, Phone, AtSign, ShieldAlert,
  Loader2, FileSignature, Eye, Sparkles, Link2, Copy, Calendar,
  ArrowLeftRight,
} from "lucide-react";
import RicetteIA from "./deblistering/ricette/RicetteIA";
import TerapieDeblistering from "./deblistering/terapie/TerapieDeblistering";
import MediciCurantiSection from "../components/doctors/MediciCurantiSection";
import ContrattiPrivacyStruttura from "../components/deblistering/ContrattiPrivacyStruttura";
import { useAuth } from "../context/AuthContext";
import { canAccessDeblistering } from "../utils/deblisteringAccess";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Btn({ children, onClick, disabled, variant = "primary", className = "", ...props }) {
  const base = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-cyan-600 text-white hover:bg-cyan-700",
    secondary: "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200",
    danger: "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200",
    success: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200",
    ghost: "text-slate-600 hover:bg-slate-100",
  };
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
}

function InputField({ label, value, onChange, type = "text", placeholder, icon: Icon, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${Icon ? "pl-10" : ""}`}
          {...props}
        />
      </div>
    </div>
  );
}

/** Evidenzia in grassetto il dominio (es. email.affidya.it) nell'indirizzo inbox struttura. */
function DeblisteringStrutturaEmailDisplay({ email }) {
  if (!email) return <span className="font-mono break-all">—</span>;
  const at = email.lastIndexOf("@");
  if (at < 0) return <span className="font-mono break-all">{email}</span>;
  return (
    <span className="font-mono break-all">
      {email.slice(0, at + 1)}
      <strong className="font-semibold text-slate-900">{email.slice(at + 1)}</strong>
    </span>
  );
}

// ========== IMPOSTAZIONI TAB ==========
function ImpostazioniTab({ token }) {
  const { hasPermission, activeStruttura } = useAuth();
  const canManageDoctors = hasPermission("struttura:update");
  const [config, setConfig] = useState({
    prezzo_blisteraggio: 0, farmacia_nome: "", farmacia_email: "",
    farmacia_telefono: "", farmacia_indirizzo: "", farmacia_pec: "",
    farmacia_referente: "", email_struttura_affidya: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Delega template state
  const [delegaBodyText, setDelegaBodyText] = useState("");
  const [delegaHasLogo, setDelegaHasLogo] = useState(false);
  const [delegaLogoRemoved, setDelegaLogoRemoved] = useState(false);
  const [delegaLogoFile, setDelegaLogoFile] = useState(null);
  const [delegaLogoPreview, setDelegaLogoPreview] = useState(null);
  const [delegaLogoSavedUrl, setDelegaLogoSavedUrl] = useState(null);
  const [delegaLoading, setDelegaLoading] = useState(true);
  const [delegaSaving, setDelegaSaving] = useState(false);
  const [delegaExtracting, setDelegaExtracting] = useState(false);
  const delegaFileRef = useRef(null);
  const delegaLogoInputRef = useRef(null);

  const fetchDelegaLogo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/delega-template/logo?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      setDelegaLogoSavedUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch {
      setDelegaLogoSavedUrl(null);
    }
  }, [token, activeStruttura?.id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/deblistering/config`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.config) setConfig(prev => ({ ...prev, ...res.data.config }));
      } catch (e) {
        toast.error("Errore caricamento configurazione");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, activeStruttura?.id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/deblistering/delega-template`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDelegaBodyText(res.data.body_text || "");
        setDelegaHasLogo(!!res.data.has_logo);
        if (res.data.has_logo) fetchDelegaLogo();
      } catch (e) {
        // non critico
      } finally {
        setDelegaLoading(false);
      }
    })();
  }, [token, activeStruttura?.id, fetchDelegaLogo]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/deblistering/config`, config, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDelegaTemplate = async () => {
    if (!delegaBodyText.trim()) {
      toast.error("Il testo della delega non può essere vuoto");
      return;
    }
    setDelegaSaving(true);
    try {
      const fd = new FormData();
      fd.append("body_text", delegaBodyText);
      if (delegaLogoFile) fd.append("logo", delegaLogoFile);
      if (delegaLogoRemoved) fd.append("remove_logo", "true");
      const res = await axios.post(`${API_URL}/api/deblistering/delega-template`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDelegaHasLogo(res.data.has_logo ?? false);
      setDelegaLogoRemoved(false);
      if (delegaLogoFile) {
        setDelegaLogoFile(null);
        setDelegaLogoPreview(null);
        fetchDelegaLogo();
      }
      toast.success("Template delega salvato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio template");
    } finally {
      setDelegaSaving(false);
    }
  };

  const handleDelegaLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Il logo deve essere PNG, JPG o WEBP");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Il logo non può superare 2 MB");
      return;
    }
    setDelegaLogoFile(file);
    setDelegaLogoRemoved(false);
    const reader = new FileReader();
    reader.onload = (ev) => setDelegaLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveDelegaLogo = () => {
    setDelegaLogoFile(null);
    setDelegaLogoPreview(null);
    setDelegaLogoSavedUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setDelegaHasLogo(false);
    setDelegaLogoRemoved(true);
  };

  const handleDeleteDelegaTemplate = async () => {
    if (!window.confirm("Eliminare il template della delega?")) return;
    try {
      await axios.delete(`${API_URL}/api/deblistering/delega-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDelegaBodyText("");
      handleRemoveDelegaLogo();
      toast.success("Template delega eliminato");
    } catch (e) {
      toast.error("Errore eliminazione template");
    }
  };

  const handleImportDelegaPDF = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDelegaExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API_URL}/api/deblistering/delega-template/extract-text`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setDelegaBodyText(res.data.text || "");
      toast.success("Testo estratto dal PDF. Verifica e salva.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore estrazione testo PDF");
    } finally {
      setDelegaExtracting(false);
      if (delegaFileRef.current) delegaFileRef.current.value = "";
    }
  };

  if (loading || delegaLoading) return <LoadingSpinner />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Euro className="w-5 h-5 text-cyan-600" />
            Prezzo Blisteraggio
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InputField
            label="Prezzo mensile per ospite (€)"
            type="number"
            value={config.prezzo_blisteraggio}
            onChange={e => setConfig({ ...config, prezzo_blisteraggio: parseFloat(e.target.value) || 0 })}
            placeholder="0.00"
            icon={Euro}
          />
          <p className="text-xs text-slate-500">
            Questo importo verrà addebitato in fattura per ogni ospite aderente al servizio di deblistering.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5 text-cyan-600" />
            Email Struttura Affidya
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email ricezione fatture farmacia</label>
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800">
              <AtSign className="w-4 h-4 text-slate-400 shrink-0" />
              <DeblisteringStrutturaEmailDisplay email={config.email_struttura_affidya} />
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Comunica questa email alla farmacia:
            le fatture inviate qui vengono importate in Affidya.
          </p>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Building2 className="w-5 h-5 text-cyan-600" />
            Contatti Farmacia
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Nome farmacia"
              value={config.farmacia_nome}
              onChange={e => setConfig({ ...config, farmacia_nome: e.target.value })}
              placeholder="Farmacia..."
              icon={Building2}
            />
            <InputField
              label="Referente"
              value={config.farmacia_referente}
              onChange={e => setConfig({ ...config, farmacia_referente: e.target.value })}
              placeholder="Nome referente"
              icon={Users}
            />
            <InputField
              label="Email blisteraggio"
              value={config.farmacia_email}
              onChange={e => setConfig({ ...config, farmacia_email: e.target.value })}
              placeholder="blisteraggio@farmacia.it"
              icon={Mail}
            />
            <InputField
              label="PEC"
              value={config.farmacia_pec}
              onChange={e => setConfig({ ...config, farmacia_pec: e.target.value })}
              placeholder="farmacia@pec.it"
              icon={Mail}
            />
            <InputField
              label="Telefono"
              value={config.farmacia_telefono}
              onChange={e => setConfig({ ...config, farmacia_telefono: e.target.value })}
              placeholder="0123456789"
              icon={Phone}
            />
            <InputField
              label="Indirizzo"
              value={config.farmacia_indirizzo}
              onChange={e => setConfig({ ...config, farmacia_indirizzo: e.target.value })}
              placeholder="Via..."
              icon={Building2}
            />
          </div>
          <div className="mt-6 flex justify-end">
            <Btn onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {saving ? "Salvataggio..." : "Salva configurazione"}
            </Btn>
          </div>
        </CardContent>
      </Card>

      <ContrattiPrivacyStruttura token={token} />

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-cyan-600" />
            Template Delega Medico
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">
            Configura logo e testo della delega per la firma digitale. Il PDF generato include
            il logo in intestazione, i dati dell'ospite e del medico, il testo della delega e la firma.
          </p>

          {/* Logo */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Logo struttura</label>
            <p className="text-xs text-slate-500">PNG, JPG o WEBP — max 2 MB. Appare in cima al PDF della delega.</p>
            {(delegaLogoPreview || delegaLogoSavedUrl) ? (
              <div className="flex items-center gap-3">
                <img
                  src={delegaLogoPreview || delegaLogoSavedUrl}
                  alt="Logo delega"
                  className="h-14 max-w-[160px] object-contain border border-slate-200 rounded p-1 bg-white"
                />
                <div className="flex flex-col gap-1">
                  <Btn variant="secondary" className="text-xs py-1 px-2" onClick={() => delegaLogoInputRef.current?.click()}>
                    <Upload className="w-3.5 h-3.5" />
                    Sostituisci
                  </Btn>
                  <Btn variant="ghost" className="text-xs py-1 px-2 text-red-600" onClick={handleRemoveDelegaLogo}>
                    <Trash2 className="w-3.5 h-3.5" />
                    Rimuovi
                  </Btn>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => delegaLogoInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 rounded-lg p-5 text-center hover:border-cyan-400 transition-colors"
              >
                <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">Carica logo (opzionale)</p>
              </button>
            )}
            <input
              ref={delegaLogoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              onChange={handleDelegaLogoChange}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              ref={delegaFileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleImportDelegaPDF}
            />
            <Btn
              variant="secondary"
              onClick={() => delegaFileRef.current?.click()}
              disabled={delegaExtracting}
            >
              {delegaExtracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {delegaExtracting ? "Estrazione in corso..." : "Importa da PDF"}
            </Btn>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Testo della delega</label>
            <textarea
              value={delegaBodyText}
              onChange={e => setDelegaBodyText(e.target.value)}
              rows={12}
              placeholder="Inserisci qui il testo della delega medico..."
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-y font-mono"
            />
          </div>
          {delegaBodyText.trim() && (
            <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="text-sm text-emerald-700">Template configurato — {delegaBodyText.trim().split("\n").length} righe</span>
            </div>
          )}
          <div className="flex flex-wrap justify-between gap-2">
            {delegaBodyText.trim() && (
              <Btn variant="danger" onClick={handleDeleteDelegaTemplate}>
                <Trash2 className="w-4 h-4" />
                Elimina template
              </Btn>
            )}
            <Btn onClick={handleSaveDelegaTemplate} disabled={delegaSaving} className="ml-auto">
              {delegaSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {delegaSaving ? "Salvataggio..." : "Salva template delega"}
            </Btn>
          </div>
        </CardContent>
      </Card>

      <MediciCurantiSection token={token} canManage={canManageDoctors} />
    </div>
  );
}

// ========== OSPITI TAB ==========
function OspitiTab({ token }) {
  const navigate = useNavigate();
  const { activeStruttura } = useAuth();
  const [ospiti, setOspiti] = useState([]);
  const [farmaciaEmail, setFarmaciaEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState(null);
  const [inviaDialog, setInviaDialog] = useState({
    open: false, fascicoloId: null, messaggio: "", inviataAt: null,
    farmaciaEmail: "", familiareEmail: "",
  });
  const [linkDialog, setLinkDialog] = useState({
    open: false,
    fascicoloId: null,
    ospiteLabel: "",
    email: "",
    nomeDestinatario: "",
    messaggio: "",
    portalUrl: "",
    linkStato: null,
    linkInviataAt: null,
  });
  const [sending, setSending] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [deletingDelega, setDeletingDelega] = useState(null);

  const fetchOspiti = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/ospiti`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOspiti(res.data.ospiti || []);
      setFarmaciaEmail(res.data.farmacia_email || "");
    } catch (e) {
      toast.error("Errore caricamento ospiti");
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id]);

  useEffect(() => { fetchOspiti(); }, [fetchOspiti]);

  const handleToggle = async (fascicolo_id, nuovoStato) => {
    setToggling(fascicolo_id);
    try {
      await axios.post(
        `${API_URL}/api/deblistering/ospiti/${fascicolo_id}/toggle`,
        { attivo: nuovoStato },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOspiti(prev => prev.map(o =>
        o.fascicolo_id === fascicolo_id ? { ...o, deblistering_attivo: nuovoStato } : o
      ));
      toast.success(nuovoStato ? "Deblistering attivato" : "Deblistering disattivato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setToggling(null);
    }
  };

  const openInviaDialog = (o) => {
    setInviaDialog({
      open: true,
      fascicoloId: o.fascicolo_id,
      farmaciaEmail: farmaciaEmail || "",
      familiareEmail: o.familiare_email || "",
      messaggio: "",
      inviataAt: o.delega_inviata_medico_at || null,
    });
  };

  const openLinkDialog = async (o) => {
    setLinkDialog({
      open: true,
      fascicoloId: o.fascicolo_id,
      ospiteLabel: `${o.cognome} ${o.nome}`.trim(),
      email: o.familiare_email || "",
      nomeDestinatario: "",
      messaggio: "",
      portalUrl: "",
      linkStato: o.delega_link_stato || null,
      linkInviataAt: o.delega_link_inviata_at || null,
    });
    try {
      const res = await axios.get(
        `${API_URL}/api/deblistering/delega/${o.fascicolo_id}/link-firma`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const link = res.data?.link;
      if (link) {
        setLinkDialog(prev => ({
          ...prev,
          email: prev.email || link.email || "",
          nomeDestinatario: link.nome_destinatario || "",
          portalUrl: link.portal_url || "",
          linkStato: link.stato || prev.linkStato,
          linkInviataAt: link.created_at || prev.linkInviataAt,
        }));
      }
    } catch {
      // dialog resta apribile con dati fascicolo
    }
  };

  const handleInviaLinkFirma = async () => {
    if (!linkDialog.email?.trim()) {
      toast.error("Inserisci l'email del familiare");
      return;
    }
    setSendingLink(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/deblistering/delega/${linkDialog.fascicoloId}/invia-link-firma`,
        {
          email: linkDialog.email.trim(),
          nome_destinatario: linkDialog.nomeDestinatario.trim() || null,
          messaggio: linkDialog.messaggio.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Link inviato al familiare");
      setLinkDialog(prev => ({
        ...prev,
        portalUrl: res.data.portal_url || prev.portalUrl,
        linkStato: "inviata",
        linkInviataAt: new Date().toISOString(),
      }));
      fetchOspiti();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio link");
    } finally {
      setSendingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (!linkDialog.portalUrl) {
      toast.error("Nessun link disponibile. Invia prima il link al familiare.");
      return;
    }
    navigator.clipboard.writeText(linkDialog.portalUrl).then(
      () => toast.success("Link copiato negli appunti"),
      () => toast.error("Impossibile copiare il link"),
    );
  };

  const handleInviaDelega = async () => {
    if (!inviaDialog.farmaciaEmail.trim() && !inviaDialog.familiareEmail.trim()) {
      toast.error("Nessun destinatario disponibile. Impostare l'email blisteraggio in Deblistering o l'email del familiare nel fascicolo.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/deblistering/delega/${inviaDialog.fascicoloId}/invia-medico`,
        { messaggio: inviaDialog.messaggio || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data.message || "Delega inviata");
      setInviaDialog(prev => ({ ...prev, open: false }));
      fetchOspiti();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio email");
    } finally {
      setSending(false);
    }
  };

  const handleViewPdf = async (fascicoloId) => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/delega/${fascicoloId}/download?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Errore apertura PDF delega");
    }
  };

  const handleDeleteDelega = async (fascicoloId) => {
    if (!window.confirm("Eliminare la delega firmata? Potrai firmarla nuovamente.")) return;
    setDeletingDelega(fascicoloId);
    try {
      await axios.delete(`${API_URL}/api/deblistering/delega/${fascicoloId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Delega eliminata");
      fetchOspiti();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione delega");
    } finally {
      setDeletingDelega(null);
    }
  };

  const filtered = ospiti.filter(o => {
    const fullName = `${o.nome} ${o.cognome} ${o.camera}`.toLowerCase();
    return fullName.includes(search.toLowerCase());
  });

  const sortedFiltered = [...filtered].sort((a, b) => {
    const byCognome = (a.cognome || "").localeCompare(b.cognome || "", "it", { sensitivity: "base" });
    if (byCognome !== 0) return byCognome;
    return (a.nome || "").localeCompare(b.nome || "", "it", { sensitivity: "base" });
  });

  const attivi = ospiti.filter(o => o.deblistering_attivo).length;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca ospite..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>
        <Badge className="bg-cyan-100 text-cyan-700 px-3 py-1">
          {attivi} ospiti aderenti su {ospiti.length}
        </Badge>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left p-3 font-medium text-slate-600">Ospite</th>
                  <th className="text-left p-3 font-medium text-slate-600">Camera</th>
                  <th className="text-center p-3 font-medium text-slate-600">Deblistering</th>
                  <th className="text-left p-3 font-medium text-slate-600">Delega farmacia</th>
                </tr>
              </thead>
              <tbody>
                {sortedFiltered.map(o => (
                  <tr key={o.fascicolo_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="p-3">
                      <span className="font-medium text-slate-800">{o.cognome} {o.nome}</span>
                    </td>
                    <td className="p-3 text-slate-600">{o.camera || "—"}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={o.deblistering_attivo}
                          onCheckedChange={v => handleToggle(o.fascicolo_id, v)}
                          disabled={toggling === o.fascicolo_id}
                          className={o.deblistering_attivo ? "data-[state=checked]:bg-cyan-600" : ""}
                        />
                        {toggling === o.fascicolo_id && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                      </div>
                    </td>
                    <td className="p-3">
                      {!o.deblistering_attivo ? (
                        <span className="text-slate-400 text-xs">—</span>
                      ) : !o.delega_firmata_at ? (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex flex-wrap gap-1.5">
                            <Btn
                              variant="secondary"
                              className="text-xs py-1 px-2"
                              onClick={() => navigate(`/deblistering/ospiti/${o.fascicolo_id}/firma-delega`)}
                            >
                              <FileSignature className="w-3.5 h-3.5" />
                              Firma in sede
                            </Btn>
                            <Btn
                              variant="primary"
                              className="text-xs py-1 px-2"
                              onClick={() => openLinkDialog(o)}
                            >
                              <Link2 className="w-3.5 h-3.5" />
                              Invia link familiare
                            </Btn>
                          </div>
                          {o.delega_link_stato && ["inviata", "consegnata"].includes(o.delega_link_stato) && (
                            <span className="text-xs text-cyan-700">
                              Link {o.delega_link_stato === "consegnata" ? "aperto" : "inviato"}
                              {o.delega_link_inviata_at && (
                                <> il {new Date(o.delega_link_inviata_at).toLocaleDateString("it-IT")}</>
                              )}
                              {o.delega_link_email && <> — {o.delega_link_email}</>}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-1 text-xs text-emerald-700">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span>Firmata il {new Date(o.delega_firmata_at).toLocaleDateString("it-IT")}</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <Btn
                              variant="secondary"
                              className="text-xs py-1 px-2"
                              onClick={() => handleViewPdf(o.fascicolo_id)}
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Visualizza PDF
                            </Btn>
                            <Btn
                              variant={o.delega_inviata_medico_at ? "ghost" : "success"}
                              className="text-xs py-1 px-2"
                              onClick={() => openInviaDialog(o)}
                            >
                              <Mail className="w-3.5 h-3.5" />
                              {o.delega_inviata_medico_at ? "Re-invia delega" : "Invia delega"}
                            </Btn>
                            <Btn
                              variant="danger"
                              className="text-xs py-1 px-2"
                              onClick={() => handleDeleteDelega(o.fascicolo_id)}
                              disabled={deletingDelega === o.fascicolo_id}
                            >
                              {deletingDelega === o.fascicolo_id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                              Elimina
                            </Btn>
                          </div>
                          {o.delega_inviata_medico_at && (
                            <span className="text-xs text-slate-400">
                              Inviata {new Date(o.delega_inviata_medico_at).toLocaleDateString("it-IT")}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {sortedFiltered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-slate-400">
                      Nessun ospite trovato
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Invia delega */}
      <Dialog open={inviaDialog.open} onOpenChange={open => setInviaDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-600" />
              Invia delega
            </DialogTitle>
            <DialogDescription>
              Il PDF della delega firmata verrà inviato ai destinatari indicati sotto.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {inviaDialog.inviataAt && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                Già inviata il {new Date(inviaDialog.inviataAt).toLocaleDateString("it-IT")} — stai effettuando un re-invio.
              </div>
            )}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Destinatari</label>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm space-y-1">
                <div className="flex items-start gap-2">
                  <span className="text-slate-500 shrink-0">Farmacia:</span>
                  <span className="font-medium text-slate-800 break-all">
                    {inviaDialog.farmaciaEmail || "— non configurata —"}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-500 shrink-0">Familiare:</span>
                  <span className="font-medium text-slate-800 break-all">
                    {inviaDialog.familiareEmail || "— non impostata —"}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Messaggio aggiuntivo (opzionale)
              </label>
              <textarea
                value={inviaDialog.messaggio}
                onChange={e => setInviaDialog(prev => ({ ...prev, messaggio: e.target.value }))}
                rows={3}
                placeholder="Messaggio personalizzato da includere nell'email..."
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setInviaDialog(prev => ({ ...prev, open: false }))}>
              Annulla
            </Btn>
            <Btn
              onClick={handleInviaDelega}
              disabled={sending || (!inviaDialog.farmaciaEmail.trim() && !inviaDialog.familiareEmail.trim())}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Invio in corso..." : "Invia delega"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Invia link firma familiare */}
      <Dialog open={linkDialog.open} onOpenChange={open => setLinkDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-cyan-600" />
              Invia link firma al familiare
            </DialogTitle>
            <DialogDescription>
              Il familiare riceverà un link sicuro per visionare e firmare la delega deblistering
              {linkDialog.ospiteLabel ? ` per ${linkDialog.ospiteLabel}` : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {linkDialog.linkInviataAt && linkDialog.linkStato && ["inviata", "consegnata"].includes(linkDialog.linkStato) && (
              <div className="flex items-center gap-2 p-2 bg-cyan-50 rounded-lg border border-cyan-200 text-sm text-cyan-800">
                <AlertCircle className="w-4 h-4 shrink-0" />
                Link già inviato il {new Date(linkDialog.linkInviataAt).toLocaleDateString("it-IT")} — un nuovo invio sostituirà il link precedente.
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email familiare *</label>
              <input
                type="email"
                value={linkDialog.email}
                onChange={e => setLinkDialog(prev => ({ ...prev, email: e.target.value }))}
                placeholder="familiare@esempio.it"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome destinatario</label>
              <input
                value={linkDialog.nomeDestinatario}
                onChange={e => setLinkDialog(prev => ({ ...prev, nomeDestinatario: e.target.value }))}
                placeholder="Es. Mario Rossi"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Messaggio aggiuntivo (opzionale)</label>
              <textarea
                value={linkDialog.messaggio}
                onChange={e => setLinkDialog(prev => ({ ...prev, messaggio: e.target.value }))}
                rows={3}
                placeholder="Messaggio personalizzato da includere nell'email..."
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
              />
            </div>
            {linkDialog.portalUrl && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs break-all text-slate-600">
                {linkDialog.portalUrl}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {linkDialog.portalUrl && (
              <Btn variant="secondary" onClick={handleCopyLink} className="sm:mr-auto">
                <Copy className="w-4 h-4" />
                Copia link
              </Btn>
            )}
            <Btn variant="secondary" onClick={() => setLinkDialog(prev => ({ ...prev, open: false }))}>
              Annulla
            </Btn>
            <Btn onClick={handleInviaLinkFirma} disabled={sendingLink || !linkDialog.email?.trim()}>
              {sendingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sendingLink ? "Invio in corso..." : "Invia link"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== DOCUMENTI TAB (contratti, terapie) ==========
function DocumentiTab({ token, tipo, tipoLabel, icon: TabIcon, showCambioDettagli = false, showFirma = false }) {
  const { activeStruttura } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [ospiti, setOspiti] = useState([]);
  const [form, setForm] = useState({ fascicolo_id: "", note: "", cambio_dettagli: "", ente_emittente: "medico", firmato: false, file: null });
  const [sending, setSending] = useState(null);
  const [sendDialog, setSendDialog] = useState({ open: false, docId: null, messaggio: "" });

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/documenti?tipo=${tipo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs(res.data.documenti || []);
    } catch (e) {
      toast.error("Errore caricamento documenti");
    } finally {
      setLoading(false);
    }
  }, [token, tipo, activeStruttura?.id]);

  const fetchOspiti = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/ospiti`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOspiti(res.data.ospiti || []);
    } catch (e) { /* ignore */ }
  }, [token, activeStruttura?.id]);

  useEffect(() => { fetchDocs(); fetchOspiti(); }, [fetchDocs, fetchOspiti]);

  const handleUpload = async () => {
    if (!form.file) { toast.error("Seleziona un file"); return; }
    if (tipo === "cambio_terapia") {
      if (!form.fascicolo_id) { toast.error("Seleziona l'ospite"); return; }
      if (!form.ente_emittente) { toast.error("Indica se il documento è firmato dal medico o dall'ASL"); return; }
      if (!form.firmato) { toast.error("Conferma che il documento è firmato"); return; }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("file", form.file);
      if (form.fascicolo_id) fd.append("fascicolo_id", form.fascicolo_id);
      if (form.note) fd.append("note", form.note);
      if (form.cambio_dettagli) fd.append("cambio_dettagli", form.cambio_dettagli);
      if (tipo === "cambio_terapia") {
        fd.append("ente_emittente", form.ente_emittente);
        fd.append("firmato", "true");
      }
      await axios.post(`${API_URL}/api/deblistering/documenti/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success(`${tipoLabel} caricato`);
      setShowUpload(false);
      setForm({ fascicolo_id: "", note: "", cambio_dettagli: "", ente_emittente: "medico", firmato: false, file: null });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try {
      await axios.delete(`${API_URL}/api/deblistering/documenti/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Documento eliminato");
      fetchDocs();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const handleSend = async () => {
    setSending(sendDialog.docId);
    try {
      await axios.post(
        `${API_URL}/api/deblistering/documenti/${sendDialog.docId}/invia-farmacia`,
        { messaggio: sendDialog.messaggio || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Documento inviato alla farmacia");
      setSendDialog({ open: false, docId: null, messaggio: "" });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio");
    } finally {
      setSending(null);
    }
  };

  const handleFirma = async (docId) => {
    if (!window.confirm("Confermi la firma come struttura?")) return;
    try {
      await axios.post(`${API_URL}/api/deblistering/documenti/${docId}/firma-struttura`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Contratto firmato");
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore firma");
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/documenti/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download");
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <TabIcon className="w-5 h-5 text-cyan-600" />
          {tipoLabel}
        </h3>
        <Btn onClick={() => setShowUpload(true)}>
          <Upload className="w-4 h-4" /> Carica {tipoLabel}
        </Btn>
      </div>

      {/* Upload dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Carica {tipoLabel}</DialogTitle>
            <DialogDescription>Seleziona il file e l'ospite associato.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ospite</label>
              <select
                value={form.fascicolo_id}
                onChange={e => setForm({ ...form, fascicolo_id: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">— Nessun ospite (contratto struttura) —</option>
                {ospiti.map(o => (
                  <option key={o.fascicolo_id} value={o.fascicolo_id}>
                    {o.cognome} {o.nome} {o.camera ? `(${o.camera})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">File</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={e => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
              />
            </div>
            {showCambioDettagli && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Firmato da *</label>
                  <select
                    value={form.ente_emittente}
                    onChange={e => setForm({ ...form, ente_emittente: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="medico">Medico</option>
                    <option value="asl">ASL</option>
                  </select>
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.firmato}
                    onChange={e => setForm({ ...form, firmato: e.target.checked })}
                    className="mt-1"
                  />
                  <span>Confermo che il documento è firmato dal medico o dall&apos;ASL</span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Dettagli cambio terapia
                  </label>
                  <textarea
                    value={form.cambio_dettagli}
                    onChange={e => setForm({ ...form, cambio_dettagli: e.target.value })}
                    rows={3}
                    placeholder="Descrivi le modifiche alla terapia..."
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
              <textarea
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
                rows={2}
                placeholder="Note opzionali..."
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setShowUpload(false)}>Annulla</Btn>
            <Btn onClick={handleUpload} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Caricamento..." : "Carica"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to pharmacy dialog */}
      <Dialog open={sendDialog.open} onOpenChange={v => !v && setSendDialog({ open: false, docId: null, messaggio: "" })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invia alla farmacia</DialogTitle>
            <DialogDescription>Il documento verrà inviato via email alla farmacia configurata.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Messaggio aggiuntivo (opzionale)</label>
            <textarea
              value={sendDialog.messaggio}
              onChange={e => setSendDialog({ ...sendDialog, messaggio: e.target.value })}
              rows={3}
              placeholder="Messaggio per la farmacia..."
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setSendDialog({ open: false, docId: null, messaggio: "" })}>Annulla</Btn>
            <Btn onClick={handleSend} disabled={!!sending}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Invio..." : "Invia"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents list */}
      {docs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-400">
            <TabIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nessun documento caricato</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {docs.map(d => (
            <Card key={d.id} className={d.tipo === "cambio_terapia" && d.cambio_dettagli ? "border-l-4 border-l-amber-400" : ""}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800 truncate">{d.file_name}</span>
                      {d.inviato_farmacia && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Inviato</Badge>
                      )}
                      {showFirma && d.firmato_struttura && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">Firmato</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4">
                      {d.ospite_nome && <span>Ospite: {d.ospite_cognome} {d.ospite_nome}</span>}
                      <span>{new Date(d.createdAt).toLocaleDateString("it-IT")}</span>
                      {d.note && <span className="text-slate-400">— {d.note}</span>}
                    </div>
                    {d.tipo === "cambio_terapia" && d.cambio_dettagli && (
                      <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200 text-xs text-amber-800">
                        <strong>Cambio terapia:</strong> {d.cambio_dettagli}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Btn variant="ghost" onClick={() => handleDownload(d.id, d.file_name)} title="Scarica">
                      <Download className="w-4 h-4" />
                    </Btn>
                    {showFirma && !d.firmato_struttura && (
                      <Btn variant="ghost" onClick={() => handleFirma(d.id)} title="Firma come struttura">
                        <PenLine className="w-4 h-4 text-blue-600" />
                      </Btn>
                    )}
                    <Btn
                      variant="ghost"
                      onClick={() => setSendDialog({ open: true, docId: d.id, messaggio: "" })}
                      title="Invia alla farmacia"
                    >
                      <Send className="w-4 h-4 text-cyan-600" />
                    </Btn>
                    <Btn variant="ghost" onClick={() => handleDelete(d.id)} title="Elimina">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Btn>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== FATTURE FARMACIA TAB ==========
function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(ym) {
  if (!ym || ym.length < 7) return ym || "—";
  const [y, m] = ym.split("-");
  const names = ["", "Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  const mi = parseInt(m, 10);
  return `${names[mi] || m} ${y}`;
}

function FattureFarmaciaTab({ token }) {
  const { activeStruttura } = useAuth();
  const [docs, setDocs] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ fascicolo_id: "", importo: "", mese_riferimento: "", note: "", file: null });
  const [editDialog, setEditDialog] = useState({
    open: false, doc: null, fascicolo_id: "", importo: "", mese_riferimento: "", note: "", newFile: null,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editFilePreviewUrl, setEditFilePreviewUrl] = useState(null);
  const editPreviewBlobRef = useRef(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkMese, setBulkMese] = useState(currentMonthValue());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [filterStato, setFilterStato] = useState("");
  const [reclassifyDialog, setReclassifyDialog] = useState({ open: false, doc: null, categoria: "richiesta_farmacia" });
  const [reclassifySaving, setReclassifySaving] = useState(false);
  const meseCorrente = currentMonthValue();

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/documenti?tipo=fattura_farmacia`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs(res.data.documenti || []);
    } catch (e) {
      toast.error("Errore caricamento fatture");
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id]);

  const fetchOspiti = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/ospiti`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOspiti(res.data.ospiti || []);
    } catch (e) { /* ignore */ }
  }, [token, activeStruttura?.id]);

  useEffect(() => { fetchDocs(); fetchOspiti(); }, [fetchDocs, fetchOspiti]);

  useEffect(() => {
    if (!editDialog.open || !editDialog.doc?.id) {
      if (editPreviewBlobRef.current) {
        URL.revokeObjectURL(editPreviewBlobRef.current);
        editPreviewBlobRef.current = null;
      }
      setEditFilePreviewUrl(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/deblistering/documenti/${editDialog.doc.id}/download`,
          { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
        );
        if (cancelled) return;
        const u = URL.createObjectURL(res.data);
        if (editPreviewBlobRef.current) URL.revokeObjectURL(editPreviewBlobRef.current);
        editPreviewBlobRef.current = u;
        setEditFilePreviewUrl(u);
      } catch {
        if (!cancelled) {
          if (editPreviewBlobRef.current) {
            URL.revokeObjectURL(editPreviewBlobRef.current);
            editPreviewBlobRef.current = null;
          }
          setEditFilePreviewUrl(null);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (editPreviewBlobRef.current) {
        URL.revokeObjectURL(editPreviewBlobRef.current);
        editPreviewBlobRef.current = null;
      }
      setEditFilePreviewUrl(null);
    };
  }, [editDialog.open, editDialog.doc?.id, token]);

  const [newFilePreviewUrl, setNewFilePreviewUrl] = useState(null);
  useEffect(() => {
    if (!editDialog.newFile) {
      setNewFilePreviewUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(editDialog.newFile);
    setNewFilePreviewUrl(u);
    return () => {
      URL.revokeObjectURL(u);
      setNewFilePreviewUrl(null);
    };
  }, [editDialog.newFile]);

  const handleUpload = async () => {
    if (!form.file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("tipo", "fattura_farmacia");
      fd.append("file", form.file);
      if (form.fascicolo_id) fd.append("fascicolo_id", form.fascicolo_id);
      if (form.importo) fd.append("importo", form.importo);
      if (form.mese_riferimento) fd.append("mese_riferimento", form.mese_riferimento);
      if (form.note) fd.append("note", form.note);
      await axios.post(`${API_URL}/api/deblistering/documenti/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Fattura caricata");
      setShowUpload(false);
      setForm({ fascicolo_id: "", importo: "", mese_riferimento: "", note: "", file: null });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async () => {
    setEditSaving(true);
    try {
      const fd = new FormData();
      fd.append("fascicolo_id", editDialog.fascicolo_id || "");
      if (editDialog.importo !== "" && editDialog.importo != null) fd.append("importo", editDialog.importo);
      if (editDialog.mese_riferimento) fd.append("mese_riferimento", editDialog.mese_riferimento);
      if (editDialog.note !== undefined) fd.append("note", editDialog.note);
      if (editDialog.newFile) fd.append("file", editDialog.newFile);
      await axios.patch(`${API_URL}/api/deblistering/documenti/${editDialog.doc.id}`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Fattura aggiornata");
      setEditDialog({
        open: false, doc: null, fascicolo_id: "", importo: "", mese_riferimento: "", note: "", newFile: null,
      });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setEditSaving(false);
    }
  };

  const handleInserisciFattura = async (docId) => {
    try {
      await axios.post(`${API_URL}/api/deblistering/documenti/${docId}/inserisci-fattura`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Importo segnato per inserimento in fattura");
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Eliminare questa fattura?")) return;
    try {
      await axios.delete(`${API_URL}/api/deblistering/documenti/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Fattura eliminata");
      setCheckedIds(prev => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
      fetchDocs();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const handleRiclassifica = async () => {
    if (!reclassifyDialog.doc) return;
    setReclassifySaving(true);
    try {
      await axios.post(
        `${API_URL}/api/deblistering/documenti/${reclassifyDialog.doc.id}/riclassifica`,
        { categoria: reclassifyDialog.categoria },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        reclassifyDialog.categoria === "richiesta_farmacia"
          ? "Documento spostato tra le Richieste Farmacia"
          : "Documento spostato tra le Ricette da medico",
      );
      setReclassifyDialog({ open: false, doc: null, categoria: "richiesta_farmacia" });
      setCheckedIds(prev => {
        const next = new Set(prev);
        next.delete(reclassifyDialog.doc.id);
        return next;
      });
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore riclassifica");
    } finally {
      setReclassifySaving(false);
    }
  };

  const toggleChecked = (docId) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const filteredDocs = docs.filter(d => {
    if (filterStato === "da_assegnare") return !d.fascicolo_id;
    if (filterStato === "da_inserire") return d.fascicolo_id && !d.importo_inserito_fattura;
    if (filterStato === "in_fattura") return d.importo_inserito_fattura;
    if (filterStato === "mese_passato") {
      const mr = (d.mese_riferimento || "").slice(0, 7);
      return mr && mr < meseCorrente;
    }
    return true;
  });

  const allFilteredSelected = filteredDocs.length > 0 && filteredDocs.every(d => checkedIds.has(d.id));
  const someFilteredSelected = filteredDocs.some(d => checkedIds.has(d.id));
  const selectedDocs = docs.filter(d => checkedIds.has(d.id));
  const selectedPronteInserimento = selectedDocs.filter(
    d => d.fascicolo_id && d.importo && !d.importo_inserito_fattura,
  );

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setCheckedIds(prev => {
        const next = new Set(prev);
        filteredDocs.forEach(d => next.delete(d.id));
        return next;
      });
    } else {
      setCheckedIds(prev => {
        const next = new Set(prev);
        filteredDocs.forEach(d => next.add(d.id));
        return next;
      });
    }
  };

  const handleBulkAssegnaMese = async (inserisciInFattura = false) => {
    if (checkedIds.size === 0) return;
    if (!bulkMese) {
      toast.error("Seleziona il mese di fatturazione");
      return;
    }
    if (bulkMese < meseCorrente) {
      toast.error("Il mese di fatturazione deve essere il mese corrente o un mese futuro");
      return;
    }
    if (inserisciInFattura && selectedPronteInserimento.length === 0) {
      toast.error("Le fatture selezionate devono avere ospite e importo");
      return;
    }
    const ids = inserisciInFattura
      ? selectedPronteInserimento.map(d => d.id)
      : [...checkedIds];
    if (ids.length === 0) return;

    setBulkSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/deblistering/documenti/fatture-farmacia/bulk`,
        {
          documento_ids: ids,
          mese_riferimento: bulkMese,
          inserisci_in_fattura: inserisciInFattura,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(res.data.message || "Operazione completata");
      if (res.data.errori?.length) {
        toast.warning(res.data.errori.slice(0, 3).join(" — "));
      }
      setCheckedIds(new Set());
      fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore operazione in blocco");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/documenti/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download");
    }
  };

  if (loading) return <LoadingSpinner />;

  const daAssegnare = docs.filter(d => !d.fascicolo_id).length;
  const daInserire = docs.filter(d => d.fascicolo_id && !d.importo_inserito_fattura).length;
  const mesePassatoCount = docs.filter(d => {
    const mr = (d.mese_riferimento || "").slice(0, 7);
    return mr && mr < meseCorrente;
  }).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          {daAssegnare > 0 && (
            <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
              {daAssegnare} da assegnare
            </Badge>
          )}
          {daInserire > 0 && (
            <Badge className="bg-blue-100 text-blue-700 px-3 py-1">
              {daInserire} da inserire in fattura
            </Badge>
          )}
          {mesePassatoCount > 0 && (
            <Badge className="bg-violet-100 text-violet-700 px-3 py-1">
              {mesePassatoCount} con mese fatturazione passato
            </Badge>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={filterStato}
            onChange={e => setFilterStato(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">Tutte le fatture</option>
            <option value="da_assegnare">Da assegnare (ospite)</option>
            <option value="da_inserire">Da inserire in fattura</option>
            <option value="in_fattura">Già in fattura</option>
            <option value="mese_passato">Mese fatturazione passato</option>
          </select>
          <Btn variant="secondary" onClick={fetchDocs}>
            <RefreshCw className="w-4 h-4" /> Aggiorna
          </Btn>
          <Btn onClick={() => setShowUpload(true)}>
            <Upload className="w-4 h-4" /> Carica fattura
          </Btn>
        </div>
      </div>

      {checkedIds.size > 0 && (
        <div className="flex flex-col gap-3 rounded-xl border border-cyan-200 bg-cyan-50/60 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              checked={allFilteredSelected}
              ref={(el) => {
                if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
              }}
              onChange={toggleSelectAllFiltered}
              title="Seleziona tutte le fatture filtrate"
              aria-label="Seleziona tutte"
            />
            <span className="text-sm font-medium text-cyan-900">
              {checkedIds.size} fattur{checkedIds.size === 1 ? "a selezionata" : "e selezionate"}
            </span>
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Mese fatturazione ospite</label>
              <input
                type="month"
                min={meseCorrente}
                value={bulkMese}
                onChange={e => setBulkMese(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <Btn variant="secondary" disabled={bulkSaving} onClick={() => handleBulkAssegnaMese(false)}>
              {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Assegna mese
            </Btn>
            <Btn
              disabled={bulkSaving || selectedPronteInserimento.length === 0}
              onClick={() => handleBulkAssegnaMese(true)}
              title={
                selectedPronteInserimento.length === 0
                  ? "Seleziona fatture con ospite e importo"
                  : `Assegna mese e inserisci in fattura (${selectedPronteInserimento.length})`
              }
            >
              {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
              Assegna e inserisci ({selectedPronteInserimento.length})
            </Btn>
          </div>
          <button
            type="button"
            className="text-sm text-cyan-700 hover:underline sm:ml-auto"
            onClick={() => setCheckedIds(new Set())}
          >
            Deseleziona tutte
          </button>
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Carica fattura farmacia</DialogTitle>
            <DialogDescription>Carica il PDF della fattura ricevuta dalla farmacia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ospite</label>
              <select
                value={form.fascicolo_id}
                onChange={e => setForm({ ...form, fascicolo_id: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">— Da assegnare dopo —</option>
                {ospiti.map(o => (
                  <option key={o.fascicolo_id} value={o.fascicolo_id}>
                    {o.cognome} {o.nome} {o.camera ? `(${o.camera})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <InputField
              label="Importo (€)"
              type="number"
              value={form.importo}
              onChange={e => setForm({ ...form, importo: e.target.value })}
              placeholder="0.00"
              icon={Euro}
            />
            <InputField
              label="Mese di riferimento"
              type="month"
              value={form.mese_riferimento}
              onChange={e => setForm({ ...form, mese_riferimento: e.target.value })}
            />
            <p className="text-xs text-slate-500 -mt-2">
              Mese in cui addebitare l&apos;importo sulla fattura ospite (può essere diverso dal mese della fattura farmacia).
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">File PDF</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
              <textarea
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
                rows={2}
                placeholder="Note opzionali..."
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setShowUpload(false)}>Annulla</Btn>
            <Btn onClick={handleUpload} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Caricamento..." : "Carica"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={editDialog.open}
        onOpenChange={v => {
          if (!v) {
            setEditDialog({
              open: false, doc: null, fascicolo_id: "", importo: "", mese_riferimento: "", note: "", newFile: null,
            });
          }
        }}
      >
        <DialogContent className="flex max-h-[min(90vh,760px)] w-[calc(100vw-1.25rem)] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:w-full">
          <div className="shrink-0 border-b border-slate-100 px-6 pb-4 pt-6 pr-14">
            <DialogHeader>
              <DialogTitle>Modifica fattura farmacia</DialogTitle>
              <DialogDescription className="text-xs leading-snug sm:text-sm">
                Dati e allegato. Se la voce è già in fattura ospite, descrizione e importo si aggiornano automaticamente (fatture ancora modificabili).
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
              {/* Colonna allegato */}
              <div className="space-y-3 lg:col-span-5">
                <h3 className="text-sm font-semibold text-slate-800">Allegato</h3>
                {editDialog.newFile ? (
                  <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 p-3 text-sm">
                    <p className="truncate font-medium text-slate-800" title={editDialog.newFile.name}>
                      Nuovo: {editDialog.newFile.name}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">Sostituzione al salvataggio.</p>
                    {newFilePreviewUrl && (editDialog.newFile?.type || "").startsWith("image/") && (
                      <img
                        src={newFilePreviewUrl}
                        alt="Anteprima"
                        className="mx-auto mt-2 max-h-32 rounded border border-slate-200 object-contain"
                      />
                    )}
                    <Btn variant="secondary" type="button" className="mt-2" onClick={() => setEditDialog({ ...editDialog, newFile: null })}>
                      Annulla sostituzione
                    </Btn>
                  </div>
                ) : editFilePreviewUrl && editDialog.doc ? (
                  <div className="space-y-2">
                    <p className="truncate text-xs text-slate-600" title={editDialog.doc.file_name}>{editDialog.doc.file_name}</p>
                    <div className="h-[200px] overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                      {(editDialog.doc.mime_type || "").startsWith("image/") ? (
                        <img src={editFilePreviewUrl} alt="Anteprima" className="h-full w-full object-contain" />
                      ) : (
                        <iframe title="Anteprima PDF" src={editFilePreviewUrl} className="h-full w-full border-0" />
                      )}
                    </div>
                    <Btn
                      variant="secondary"
                      type="button"
                      className="h-9 w-full text-sm sm:w-auto"
                      onClick={() => editDialog.doc && handleDownload(editDialog.doc.id, editDialog.doc.file_name)}
                    >
                      <Download className="w-4 h-4" /> Scarica
                    </Btn>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Anteprima non disponibile.</p>
                )}
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-600">Sostituisci file (PDF, immagini)</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setEditDialog({ ...editDialog, newFile: e.target.files?.[0] || null })}
                    className="w-full text-xs text-slate-600 file:mr-2 file:rounded-md file:border-0 file:bg-cyan-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-cyan-700 hover:file:bg-cyan-100 sm:text-sm file:sm:mr-4 file:sm:py-2 file:sm:text-sm"
                  />
                </div>
              </div>

              {/* Colonna dati */}
              <div className="space-y-3 lg:col-span-7 lg:border-l lg:border-slate-100 lg:pl-8">
                <h3 className="text-sm font-semibold text-slate-800">Dati fattura</h3>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Ospite</label>
                  <select
                    value={editDialog.fascicolo_id}
                    onChange={e => setEditDialog({ ...editDialog, fascicolo_id: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">— Non assegnato —</option>
                    {ospiti.map(o => (
                      <option key={o.fascicolo_id} value={o.fascicolo_id}>
                        {o.cognome} {o.nome} {o.camera ? `(${o.camera})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <InputField
                    label="Importo (€)"
                    type="number"
                    value={editDialog.importo}
                    onChange={e => setEditDialog({ ...editDialog, importo: e.target.value })}
                    placeholder="0.00"
                    icon={Euro}
                  />
                  <InputField
                    label="Mese fatturazione ospite"
                    type="month"
                    value={editDialog.mese_riferimento}
                    onChange={e => setEditDialog({ ...editDialog, mese_riferimento: e.target.value })}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Puoi spostare la voce al mese di fatturazione corrente o futuro, anche se la fattura farmacia è di mesi precedenti.
                </p>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Note</label>
                  <textarea
                    value={editDialog.note}
                    onChange={e => setEditDialog({ ...editDialog, note: e.target.value })}
                    rows={2}
                    className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t border-slate-100 bg-slate-50/80 px-6 py-4">
            <Btn
              variant="secondary"
              onClick={() => setEditDialog({
                open: false, doc: null, fascicolo_id: "", importo: "", mese_riferimento: "", note: "", newFile: null,
              })}
            >
              Annulla
            </Btn>
            <Btn onClick={handleEdit} disabled={editSaving}>
              {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {editSaving ? "Salvataggio..." : "Salva"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Riclassifica: sposta un documento finito per errore in Fatture Farmacia */}
      <Dialog
        open={reclassifyDialog.open}
        onOpenChange={(v) => {
          if (!v) setReclassifyDialog({ open: false, doc: null, categoria: "richiesta_farmacia" });
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Riclassifica documento</DialogTitle>
            <DialogDescription>
              Usa questa azione se il documento non è una fattura farmacia (es. una richiesta di
              farmaci/carepy o una ricetta del medico arrivata per email). Verrà spostato tra le
              Richieste Farmacia o le Ricette da medico, mantenendo l&apos;allegato; se era già
              stato inserito in una fattura ospite, la voce verrà rimossa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {reclassifyDialog.doc && (
              <p className="truncate text-sm text-slate-600" title={reclassifyDialog.doc.file_name}>
                {reclassifyDialog.doc.file_name}
              </p>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Sposta tra</label>
              <select
                value={reclassifyDialog.categoria}
                onChange={(e) => setReclassifyDialog({ ...reclassifyDialog, categoria: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="richiesta_farmacia">Richieste Farmacia (es. richiesta carepy)</option>
                <option value="ricette_medico">Ricette da medico (es. ricetta SSN)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Btn
              variant="secondary"
              onClick={() => setReclassifyDialog({ open: false, doc: null, categoria: "richiesta_farmacia" })}
            >
              Annulla
            </Btn>
            <Btn onClick={handleRiclassifica} disabled={reclassifySaving}>
              {reclassifySaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />}
              {reclassifySaving ? "Spostamento..." : "Sposta"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents list */}
      {docs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-400">
            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nessuna fattura farmacia</p>
            <p className="text-xs mt-1">Le fatture inviate dalla farmacia all'email della struttura appariranno qui automaticamente.</p>
          </CardContent>
        </Card>
      ) : filteredDocs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-400">
            <p>Nessuna fattura corrisponde al filtro selezionato.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredDocs.length > 1 && checkedIds.size === 0 && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none px-1">
              <input
                type="checkbox"
                className="rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                checked={allFilteredSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                }}
                onChange={toggleSelectAllFiltered}
              />
              Seleziona tutte ({filteredDocs.length})
            </label>
          )}
          {filteredDocs.map(d => {
            const mr = (d.mese_riferimento || "").slice(0, 7);
            const mesePassato = mr && mr < meseCorrente;
            return (
            <Card
              key={d.id}
              className={`${checkedIds.has(d.id) ? "ring-2 ring-cyan-300" : ""} ${!d.fascicolo_id ? "border-l-4 border-l-amber-400" : d.importo_inserito_fattura ? "border-l-4 border-l-emerald-400" : ""}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <input
                      type="checkbox"
                      className="mt-1 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 shrink-0"
                      checked={checkedIds.has(d.id)}
                      onChange={() => toggleChecked(d.id)}
                      aria-label={`Seleziona ${d.file_name}`}
                    />
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800 truncate">{d.file_name}</span>
                      {d.importo_inserito_fattura && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">In fattura</Badge>
                      )}
                      {d.pagata && (
                        <Badge className="bg-teal-100 text-teal-700 text-xs">Pagata (farmacia)</Badge>
                      )}
                      {d.origine === "portale_farmacia" && (
                        <Badge className="bg-slate-100 text-slate-600 text-xs">Portale farmacia</Badge>
                      )}
                      {d.email_source && (
                        <Badge className="bg-purple-100 text-purple-700 text-xs">Via email</Badge>
                      )}
                      {!d.fascicolo_id && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Da assegnare</Badge>
                      )}
                      {mesePassato && !d.importo_inserito_fattura && (
                        <Badge className="bg-violet-100 text-violet-700 text-xs">Mese passato</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4">
                      {d.ospite_nome && <span>Ospite: {d.ospite_cognome} {d.ospite_nome}</span>}
                      {d.importo != null && <span className="font-medium text-slate-700">€{Number(d.importo).toFixed(2)}</span>}
                      {d.mese_riferimento && (
                        <span>Fatt. ospite: {formatMonthLabel(mr)}</span>
                      )}
                      <span>Ricevuta: {new Date(d.createdAt).toLocaleDateString("it-IT")}</span>
                    </div>
                    {d.note && <p className="text-xs text-slate-400 mt-1">{d.note}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 md:pl-0 pl-8">
                    <Btn variant="ghost" onClick={() => handleDownload(d.id, d.file_name)} title="Scarica">
                      <Download className="w-4 h-4" />
                    </Btn>
                    <Btn
                      variant="ghost"
                      onClick={() => setEditDialog({
                        open: true, doc: d,
                        fascicolo_id: d.fascicolo_id || "",
                        importo: d.importo ?? "",
                        mese_riferimento: d.mese_riferimento || "",
                        note: d.note || "",
                        newFile: null,
                      })}
                      title="Modifica"
                    >
                      <PenLine className="w-4 h-4 text-blue-600" />
                    </Btn>
                    {d.fascicolo_id && d.importo && !d.importo_inserito_fattura && (
                      <Btn variant="ghost" onClick={() => handleInserisciFattura(d.id)} title="Inserisci in fattura">
                        <FileCheck className="w-4 h-4 text-emerald-600" />
                      </Btn>
                    )}
                    <Btn
                      variant="ghost"
                      onClick={() => setReclassifyDialog({ open: true, doc: d, categoria: "richiesta_farmacia" })}
                      title="Non è una fattura: sposta tra Richieste Farmacia o Ricette da medico"
                    >
                      <ArrowLeftRight className="w-4 h-4 text-amber-600" />
                    </Btn>
                    <Btn variant="ghost" onClick={() => handleDelete(d.id)} title="Elimina">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Btn>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ========== LOADING SPINNER ==========
function LoadingSpinner() {
  return (
    <div className="flex justify-center py-12">
      <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
    </div>
  );
}

// ========== ACCESS DENIED ==========
function AccessDenied() {
  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-3 md:p-5 lg:p-6 max-w-[1580px] mx-auto">
        <PageHeader
          title="Deblistering"
          subtitle="Gestione blister farmaci personalizzati"
          icon={Package}
          iconContainerClassName="bg-gradient-to-r from-teal-500 to-cyan-600"
          iconClassName="text-white"
        />
      </div>
      <div className="max-w-[1580px] mx-auto px-4 py-8">
        <Card className="max-w-lg mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Accesso non autorizzato</h2>
            <p className="text-slate-500">
              Il modulo Deblistering non è abilitato per il tuo account.
              Contatta l'amministratore per richiedere l'accesso.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ========== MAIN PAGE ==========
export default function Deblistering() {
  const { user, token, activeStruttura } = useAuth();
  const [searchParams] = useSearchParams();
  const [riepilogo, setRiepilogo] = useState(null);
  const tabParam = searchParams.get("tab") || "impostazioni";
  const [activeTab, setActiveTab] = useState(tabParam === "contratti" ? "impostazioni" : tabParam);

  const hasAccess = canAccessDeblistering(user);

  const fetchRiepilogo = useCallback(async () => {
    if (!hasAccess || !token) return;
    try {
      const res = await axios.get(`${API_URL}/api/deblistering/riepilogo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRiepilogo(res.data);
    } catch { /* ignore */ }
  }, [hasAccess, token, activeStruttura?.id]);

  useEffect(() => { fetchRiepilogo(); }, [fetchRiepilogo]);

  if (!hasAccess) return <AccessDenied />;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-3 md:p-5 lg:p-6 max-w-[1580px] mx-auto">
        <PageHeader
          title="Deblistering"
          subtitle="Gestione blister farmaci personalizzati"
          icon={Package}
          iconContainerClassName="bg-gradient-to-r from-teal-500 to-cyan-600"
          iconClassName="text-white"
        />
      </div>

      <div className="max-w-[1580px] mx-auto px-3 md:px-5 lg:px-6 pb-8">
        {/* Stats cards — nascosti nella sezione Ricette */}
        {riepilogo && activeTab !== "ricette" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-cyan-600 mx-auto mb-1" />
                <div className="text-2xl font-bold text-slate-800">{riepilogo.ospiti_attivi}</div>
                <div className="text-xs text-slate-500">Ospiti aderenti</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Euro className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                <div className="text-2xl font-bold text-slate-800">€{(riepilogo.ricavo_mensile_stimato || 0).toFixed(2)}</div>
                <div className="text-xs text-slate-500">Ricavo mensile</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <FileText className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                <div className="text-2xl font-bold text-slate-800">{riepilogo.totale_documenti}</div>
                <div className="text-xs text-slate-500">Documenti totali</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <AlertCircle className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                <div className="text-2xl font-bold text-slate-800">
                  {(riepilogo.fatture_da_assegnare || 0) + (riepilogo.fatture_da_inserire || 0)}
                </div>
                <div className="text-xs text-slate-500">Azioni pendenti</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="bg-white border border-slate-200 rounded-t-xl overflow-hidden">
          <TabsList className="w-full h-auto flex overflow-x-auto bg-white border-b border-slate-200 p-0 rounded-none shadow-none mb-0">
            <TabsTrigger value="impostazioni" className="flex-1 min-w-fit rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-medium text-slate-600 shadow-none data-[state=active]:border-violet-600 data-[state=active]:text-violet-700 data-[state=active]:bg-transparent">
              <Settings className="w-4 h-4 mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">Impostazioni</span>
              <span className="sm:hidden">Config</span>
            </TabsTrigger>
            <TabsTrigger value="ospiti" className="flex-1 min-w-fit rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-medium text-slate-600 shadow-none data-[state=active]:border-violet-600 data-[state=active]:text-violet-700 data-[state=active]:bg-transparent">
              <Users className="w-4 h-4 mr-1.5 flex-shrink-0" />
              Ospiti
            </TabsTrigger>
            <TabsTrigger value="terapie" className="flex-1 min-w-fit rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-medium text-slate-600 shadow-none data-[state=active]:border-violet-600 data-[state=active]:text-violet-700 data-[state=active]:bg-transparent">
              <Pill className="w-4 h-4 mr-1.5 flex-shrink-0" />
              Terapie
            </TabsTrigger>
            <TabsTrigger value="ricette" className="flex-1 min-w-fit rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-medium text-slate-600 shadow-none data-[state=active]:border-violet-600 data-[state=active]:text-violet-700 data-[state=active]:bg-transparent">
              <Sparkles className="w-4 h-4 mr-1.5 flex-shrink-0" />
              Ricette
            </TabsTrigger>
            <TabsTrigger value="fatture" className="flex-1 min-w-fit rounded-none border-b-2 border-transparent bg-transparent px-4 py-3 text-sm font-medium text-slate-600 shadow-none data-[state=active]:border-violet-600 data-[state=active]:text-violet-700 data-[state=active]:bg-transparent">
              <Receipt className="w-4 h-4 mr-1.5 flex-shrink-0" />
              <span className="hidden sm:inline">Fatture Farmacia</span>
              <span className="sm:hidden">Fatture</span>
            </TabsTrigger>
          </TabsList>
          </div>

          <div className="bg-white rounded-b-xl border border-t-0 border-slate-200 p-4 md:p-6 mt-0">
          <TabsContent value="impostazioni">
            <ImpostazioniTab token={token} />
          </TabsContent>

          <TabsContent value="ospiti">
            <OspitiTab token={token} />
          </TabsContent>

          <TabsContent value="terapie">
            <TerapieDeblistering token={token} />
          </TabsContent>

          <TabsContent value="ricette">
            <RicetteIA token={token} />
          </TabsContent>

          <TabsContent value="fatture">
            <FattureFarmaciaTab token={token} />
          </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
